import React from "react"

import DebouncedInput from "../helpers/DebouncedInput"
import { parse } from "node-html-parser"

import Airtable from "airtable"

import Spinner from "./_base/Spinner"

import * as functionsLibraryStyles from "../styles/FunctionsLibrary.module.css"

const sortingSign = (sorting, field) => {
  let sortingSignStyle = ``

  if (!sorting || sorting.field !== field) return sortingSignStyle

  if (sorting.direction === `desc`)
    sortingSignStyle = functionsLibraryStyles.sortingDirectionDescending
  else sortingSignStyle = functionsLibraryStyles.sortingDirectionAscending

  return sortingSignStyle
}

const sanitizeFieldsAnchors = (fields, refWebsite) => {
  let sanitized = fields

  Object.keys(fields).forEach(f => {
    if (fields[f].includes(`href=`)) {
      const htmlContent = parse(fields[f].replace(/[\r\n]/gm, ""))
      const anchor = htmlContent && htmlContent.childNodes[0]
      if (!anchor) {
        sanitized[f] = fields[f]
        return
      }

      const href = anchor.attrs.href
      if (!href) {
        sanitized[f] = fields[f]
        return
      }

      const url = new URL(href)
      // anchor.setAttribute(`href`, refWebsite + url.pathname)
      anchor.setAttribute(`href`, `/functions-library${url.pathname}`)

      sanitized[f] = htmlContent.toString()
    }

    sanitized[f] = fields[f]
  })

  return sanitized
}

const searchEligible = (cd, token) => {
  if (!cd || !token || token === ``) return true

  return Object.keys(cd).reduce((acc, curr) => {
    return (
      acc ||
      (typeof cd[curr] === `string`
        ? cd[curr].toLowerCase().includes(token.toLowerCase())
        : false)
    )
  }, false)
}

const functionFieldLabel = `Function`
const definitionFieldLabel = `Definition`
const serverFieldLabel = `Server`
const excelFieldLabel = `Excel`
const rulesFieldLabel = `Rules`
const tiFieldLabel = `TI`
const noteFieldLabel = `Note`

export const FunctionsLibrary = props => {
  const { content } = props

  const [functionsData, setFunctionsData] = React.useState(null)
  const [sorting, setSorting] = React.useState({
    field: functionFieldLabel,
    direction: `asc`,
  })

  const [token, setSearchToken] = React.useState(``)

  const titleNode = content.childNodes.filter(node =>
    node.classNames.trim().split(/\s+/).includes("function-library-header"),
  )[0]
  const title = titleNode ? titleNode.text: ``

  const handleSortingChange = field => {
    if (sorting.field === field)
      setSorting(s => ({
        field: s.field,
        direction: s.direction === `asc` ? `desc` : `asc`,
      }))

    if (sorting.field !== field)
      setSorting({
        field: field,
        direction: `asc`,
      })
  }

  const onDataInput = async ({ value }) => {
    if (value.length > 0 && value.length < 3) return

    setSearchToken(value)
    fetchData(value)
  }

  const fetchData = React.useCallback(
    async (
      localToken = token,
      sortingField = sorting.field,
      sortingDirection = sorting.direction,
    ) => {
      const base = new Airtable({
        apiKey: `pat4bdMg0tLVaiRU1.a0e2d58a4d391abb8c677e3e595e98d726dab97623f7acd0e68ac98f5f998e1c`,
      }).base("appDxOo4hnjwgX7gS")

      setFunctionsData(null)

      let data = []
      base("TM1Functions")
        .select({
          view: "Grid view",
          sort: [{ field: sortingField, direction: sortingDirection }],
        })
        .eachPage(
          function page(records, fetchNextPage) {
            // This function (`page`) will get called for each page of records.

            records.forEach(function (record) {
              if (record.fields && searchEligible(record.fields, localToken))
                data.push(
                  sanitizeFieldsAnchors(
                    record.fields,
                    props.pageContext.codeWebsiteUrl,
                  ),
                )
            })

            // To fetch the next page of records, call `fetchNextPage`.
            // If there are more records, `page` will get called again.
            // If there are no more records, `done` will get called.
            fetchNextPage()
          },
          function done(err) {
            if (err) {
              console.error(err)
              setFunctionsData([])
              return
            }

            setFunctionsData(data)
          },
        )
    },
    [sorting, token],
  )

  React.useEffect(() => {
    const fetchInitialData = async () => {
      fetchData()
    }

    Airtable.configure({ apiKey: `key6GirdaWWWERvqx` })
    fetchInitialData().catch(console.error)
  }, [fetchData])

  return (
    <div className={`row ${functionsLibraryStyles.wrapper} ${props.className}`}>
      <div className={`col-12 ${functionsLibraryStyles.holder}`}>
        <div className={`row ${functionsLibraryStyles.headerWrapper}`}>
          <div className={`col-12 ${functionsLibraryStyles.header}`}>
            <h2>{title}</h2>
          </div>
        </div>

        <div className={`row ${functionsLibraryStyles.searchWrapper}`}>
          <div className={`col-11 col-md-6 ${functionsLibraryStyles.search}`}>
            <DebouncedInput
              className={functionsLibraryStyles.searchField}
              placeholder={`Search`}
              initialValue={``}
              isReadOnly={false}
              onDataInput={onDataInput}
            />
          </div>
        </div>

        {!functionsData && (
          <div className={`row ${functionsLibraryStyles.loadingWrapper}`}>
            <div className={`col-12 ${functionsLibraryStyles.loading}`}>
              <Spinner message={`Loading the library, tons of functions`} />
            </div>
          </div>
        )}

        {functionsData && (
          <div
            className={`row d-none d-lg-flex ${functionsLibraryStyles.functionsItemHeader}`}
          >
            <div
              className={`col-12 d-lg-none ${functionsLibraryStyles.functionsCondensedInfo}`}
            >
              Course Information
            </div>
            <div
              className={`col-3 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.function
              } ${sortingSign(sorting, functionFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(functionFieldLabel)
                }}
              >
                Function
              </a>
            </div>
            <div
              className={`col-4 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.definition
              } ${sortingSign(sorting, definitionFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(definitionFieldLabel)
                }}
              >
                Definition
              </a>
            </div>
            <div
              className={`col-1 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.server
              } ${sortingSign(sorting, serverFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(serverFieldLabel)
                }}
              >
                Tm1 Server
              </a>
            </div>
            <div
              className={`col-1 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.excel
              } ${sortingSign(sorting, excelFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(excelFieldLabel)
                }}
              >
                Excel
              </a>
            </div>
            <div
              className={`col-1 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.rules
              } ${sortingSign(sorting, rulesFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(rulesFieldLabel)
                }}
              >
                Rules
              </a>
            </div>
            <div
              className={`col-1 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.ti
              } ${sortingSign(sorting, tiFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(tiFieldLabel)
                }}
              >
                TI
              </a>
            </div>
            <div
              className={`col-1 g-0 d-none d-lg-flex ${
                functionsLibraryStyles.note
              } ${sortingSign(sorting, noteFieldLabel)}`}
            >
              <a
                href="#!"
                className={`heading-column`}
                onClick={evt => {
                  evt.preventDefault()
                  handleSortingChange(noteFieldLabel)
                }}
              >
                Note
              </a>
            </div>
          </div>
        )}

        {functionsData &&
          functionsData.length > 0 &&
          functionsData.map((cd, idx) => (
            <div
              key={idx}
              // style={{ display: searchEligible(cd, token) }}
              className={`row ${functionsLibraryStyles.courseItem} ${
                idx % 2 === 0 ? functionsLibraryStyles.evenRow : ``
              }`}
            >
              <div
                className={`col-12 d-lg-none ${functionsLibraryStyles.functionCondensedInfo}`}
              >
                <div
                  className={`row ${functionsLibraryStyles.functionAndDefinition}`}
                >
                  <div className={`col-12`}>
                    <span
                      className={`badge rounded-pill ${functionsLibraryStyles.functionName}`}
                    >
                      {cd.Function}
                    </span>
                    <br />
                    <span className={`${functionsLibraryStyles.definition}`}>
                      {cd.Definition}
                    </span>
                    <span className={`${functionsLibraryStyles.server}`}>
                      {cd.Tm1Server}
                    </span>
                  </div>
                </div>
                <div className={`row ${functionsLibraryStyles.excel}`}>
                  <div
                    className={`col-12`}
                    dangerouslySetInnerHTML={{
                      __html: `${cd.Excel || ``} ${cd.Rules || ``} ${
                        cd.TI || ``
                      }`,
                    }}
                  />
                </div>
                <div className={`row ${functionsLibraryStyles.note}`}>
                  <div
                    className={`col-12`}
                    dangerouslySetInnerHTML={{ __html: cd.Note }}
                  />
                </div>
              </div>
              <div
                className={`col-3 g-0 d-none d-lg-flex ${functionsLibraryStyles.function}`}
              >
                {cd.Function}
              </div>
              <div
                className={`col-4 g-0 d-none d-lg-flex ${functionsLibraryStyles.definition}`}
              >
                {cd.Definition}
              </div>
              <div
                className={`col-1 g-0 d-none d-lg-flex ${functionsLibraryStyles.server}`}
                dangerouslySetInnerHTML={{ __html: cd.TM1Server }}
              ></div>
              <div
                className={`col-1 g-0 d-none d-lg-flex ${functionsLibraryStyles.excel}`}
                dangerouslySetInnerHTML={{ __html: cd.Excel }}
              ></div>
              <div
                className={`col-1 g-0 d-none d-lg-flex ${functionsLibraryStyles.rules}`}
                dangerouslySetInnerHTML={{ __html: cd.Rules }}
              ></div>
              <div
                className={`col-1 g-0 d-none d-lg-flex ${functionsLibraryStyles.ti}`}
                dangerouslySetInnerHTML={{ __html: cd.TI }}
              ></div>
              <div
                className={`col-1 g-0 d-none d-lg-flex ${functionsLibraryStyles.notes}`}
                dangerouslySetInnerHTML={{ __html: cd.Note }}
              />
            </div>
          ))}
      </div>
    </div>
  )
}

export default FunctionsLibrary
